'use client';

import { useEffect, useState } from 'react';

import { api } from '@/lib/convex';
import { useSeededQueryWithStatus } from '@/lib/server-seed/seeded-convex-hooks';

import {
  resolveYourImaiPersona,
  withYourImaiMood,
  type YourImaiPersona,
} from './your-imai-persona';

/** IMAI's passing mood as the state query returns it. */
type ShownMood = { expression: string; expiresAt: number } | null | undefined;

/**
 * The mood's face while it lasts. The server leaves out a mood already
 * stale when read; this drops one that goes stale while the page is open.
 */
export function useYourImaiMoodExpression(mood: ShownMood): string | null {
  const [expiredAt, setExpiredAt] = useState<number | null>(null);
  const expiresAt = mood?.expiresAt ?? null;
  useEffect(() => {
    if (expiresAt === null) return;
    const timer = window.setTimeout(
      () => setExpiredAt(expiresAt),
      Math.max(0, expiresAt - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  return mood && expiredAt !== mood.expiresAt ? mood.expression : null;
}

/**
 * The member's IMAI as they set it up, with defaults until they do and its
 * passing mood face over the saved one, plus whether it is working right
 * now (for the header's "Thinking").
 */
export function useYourImaiPersona(
  orgSlug: string,
  defaultName: string
): {
  persona: YourImaiPersona;
  named: boolean;
  working: boolean;
  /** The saved persona has loaded (the chat exists). */
  loaded: boolean;
} {
  const state = useSeededQueryWithStatus(api.your_imai.feed.getYourImaiState, {
    orgSlug,
  });
  const stored = state.data?.persona;
  const moodExpression = useYourImaiMoodExpression(state.data?.mood);
  return {
    persona: withYourImaiMood(
      resolveYourImaiPersona(stored, defaultName),
      moodExpression
    ),
    named: Boolean(stored?.name?.trim()),
    working: state.data?.status === 'working',
    loaded: state.status === 'success' && Boolean(state.data),
  };
}
